import React from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useWhiteLabelStore } from '@/stores/whiteLabelStore';
import { useAuthStore } from '@/stores/authStore';
import { 
  LayoutDashboard, Clock, FileText, Settings, Users, Server,
  ChevronLeft, ChevronRight, AlertTriangle, MonitorCheck, Monitor,
  FolderTree, Zap, Target, History, Wrench, Filter, Activity, Tags,
  CalendarClock, Columns3, Users2, PhoneCall, Globe, Flame, Plug,
  Brain, Sparkles, Search, GitBranch, Bot, Microscope, BookOpen, Lightbulb,
  MailCheck, Gauge, BarChart3, PieChart, Building2, Radar
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const dashboards = [
  { name: 'Resumo Online', href: '/dashboard', icon: LayoutDashboard },
  { name: 'SLA', href: '/dashboard/sla-dashboard', icon: Gauge },
  { name: 'Disponibilidade', href: '/dashboard/availability', icon: Target },
  { name: 'Capacidade', href: '/dashboard/capacity', icon: BarChart3 },
  { name: 'Executivo', href: '/dashboard/executive', icon: PieChart },
  { name: 'Comparativo Mensal', href: '/dashboard/monthly', icon: BarChart3 },
  { name: 'Status Page', href: '/dashboard/status', icon: Globe },
  { name: 'Mapa de Calor', href: '/dashboard/heatmap', icon: Flame },
  { name: 'Modo NOC', href: '/noc', icon: Monitor, external: true },
];

const operations = [
  { name: 'Alertas', href: '/dashboard/incidents', icon: AlertTriangle },
  { name: 'Incidentes', href: '/dashboard/incident-board', icon: Columns3 },
  { name: 'Histórico', href: '/dashboard/incident-history', icon: History },
  { name: 'Manutenção', href: '/dashboard/maintenance', icon: Wrench },
  { name: 'Grupo de Hosts', href: '/dashboard/hostgroups', icon: FolderTree },
  { name: 'Hosts', href: '/dashboard/hosts', icon: MonitorCheck },
  { name: 'Top Triggers', href: '/dashboard/top-triggers', icon: Zap },
  { name: 'Por Período', href: '/dashboard/period', icon: Clock },
  { name: 'Relatórios', href: '/dashboard/reports', icon: FileText },
];

const aiops = [
  { name: 'Agentes AI', href: '/dashboard/aiops/agents', icon: Bot },
  { name: 'Análise Completa', href: '/dashboard/aiops/analysis', icon: Sparkles },
  { name: 'Análise Granular', href: '/dashboard/aiops/granular', icon: Microscope },
  { name: 'Análise de Histórico', href: '/dashboard/aiops/historical', icon: History },
  { name: 'Correlação de Eventos', href: '/dashboard/aiops/correlation', icon: GitBranch },
  { name: 'Predição', href: '/dashboard/aiops/prediction', icon: Brain },
  { name: 'RCA Automático', href: '/dashboard/aiops/rca', icon: Search },
  { name: 'Score de Saúde', href: '/dashboard/aiops/health', icon: Activity },
  { name: 'Anomalias', href: '/dashboard/aiops/anomalies', icon: AlertTriangle },
  { name: 'Histórico AI', href: '/dashboard/aiops/history', icon: History },
];

const webmonitor = [
  { name: 'Monitores & Domínios', href: '/dashboard/webmonitor', icon: Radar },
];

const knowledge = [
  { name: 'Soluções Técnicas', href: '/dashboard/kb/solutions', icon: BookOpen },
  { name: 'Recomendações', href: '/dashboard/kb/recommendations', icon: Lightbulb },
];

const management = [
  { name: 'Cliente/Org', href: '/dashboard/orgs', icon: Building2, badgeKey: 'orgPending' },
  { name: 'Configurações', href: '/dashboard/settings', icon: Settings },
  { name: 'Categorias', href: '/dashboard/categories', icon: Tags },
  { name: 'SLA', href: '/dashboard/sla-config', icon: Activity },
  { name: 'Filtros Salvos', href: '/dashboard/saved-filters', icon: Filter },
  { name: 'Usuários', href: '/dashboard/users', icon: Users },
  { name: 'Grupos', href: '/dashboard/groups', icon: Users2 },
  { name: 'OnCall', href: '/dashboard/oncall', icon: PhoneCall },
  { name: 'Integrações', href: '/dashboard/integrations', icon: Plug },
  { name: 'Relatórios Agendados', href: '/dashboard/scheduled-reports', icon: CalendarClock },
  { name: 'Radar Operacional', href: '/dashboard/radar', icon: Radar },
  { name: 'Resumo Executivo', href: '/dashboard/daily-summary', icon: MailCheck },
  { name: 'Zabbix API', href: '/dashboard/zabbix-config', icon: Server },
];

// NavItem component outside of Sidebar to avoid re-renders
function NavItem({ item, collapsed, location, onMobileClose, badges }) {
  const isActive = location.pathname === item.href;
  const Icon = item.icon;
  const badgeValue = item.badgeKey ? badges?.[item.badgeKey] : 0;

  const handleClick = () => {
    if (window.innerWidth < 768) {
      onMobileClose();
    }
  };

  const content = (
    <Link
      to={item.href}
      onClick={handleClick}
      data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all duration-200",
        isActive 
          ? "bg-white text-black font-medium" 
          : "text-gray-400 hover:bg-white/10 hover:text-white"
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="flex-1">{item.name}</span>}
      {!collapsed && badgeValue > 0 && (
        <Badge variant="destructive" className="h-5 px-1.5 text-[10px]">{badgeValue}</Badge>
      )}
    </Link>
  );

  if (collapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right" className="bg-gray-900 text-white border-gray-700">{item.name}</TooltipContent>
      </Tooltip>
    );
  }

  return content;
}

export function Sidebar({ collapsed, setCollapsed }) {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [dashOpen, setDashOpen] = React.useState(true);
  const [opsOpen, setOpsOpen] = React.useState(false);
  const [aiOpsOpen, setAiOpsOpen] = React.useState(false);
  const [kbOpen, setKbOpen] = React.useState(false);
  const [mgmtOpen, setMgmtOpen] = React.useState(false);
  const [webOpen, setWebOpen] = React.useState(false);
  const [badges, setBadges] = React.useState({ orgPending: 0 });
  const { config: wl } = useWhiteLabelStore();
  const { user, getAuthHeader } = useAuthStore();
  const isClient = user?.role === 'client';
  const authRef = React.useRef(getAuthHeader);
  authRef.current = getAuthHeader;

  React.useEffect(() => {
    if (isClient) return;
    let active = true;
    const load = () => {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/orgs/pending-count`, { headers: authRef.current() })
        .then(res => { if (active) setBadges({ orgPending: res.data.count || 0 }); })
        .catch(() => {});
    };
    load();
    const timer = setInterval(load, 300000);
    return () => { active = false; clearInterval(timer); };
  }, [isClient]);

  // Auto-expand section based on current page
  React.useEffect(() => {
    if (dashboards.some(m => location.pathname === m.href)) setDashOpen(true);
    if (operations.some(m => location.pathname === m.href)) setOpsOpen(true);
    if (aiops.some(m => location.pathname.startsWith(m.href))) setAiOpsOpen(true);
    if (knowledge.some(m => location.pathname.startsWith(m.href))) setKbOpen(true);
    if (management.some(m => location.pathname === m.href)) setMgmtOpen(true);
  }, [location.pathname]);

  const handleMobileClose = () => setMobileOpen(false);

  const sidebarBg = wl.primary_color && wl.primary_color !== '#000000' ? wl.primary_color : undefined;

  // Mobile sidebar overlay
  const sidebarContent = (
    <div className="flex flex-col h-full" style={sidebarBg ? { backgroundColor: sidebarBg } : undefined}>
      <div className="flex h-16 items-center border-b border-gray-800 px-4">
        <Link to="/dashboard" className="flex items-center gap-2">
          {wl.logo_url ? (
            <img src={wl.logo_url} alt={wl.app_name} className="h-7 max-w-[140px] object-contain" />
          ) : (
            <span className={cn(
              "font-bold text-xl transition-all duration-300 text-white",
              collapsed ? "text-lg" : "text-xl"
            )}>
              {collapsed ? (wl.app_name || 'O').charAt(0) : (wl.app_name || 'OneView')}
              <span style={{ color: wl.accent_color || '#EF4444' }}>.</span>
            </span>
          )}
        </Link>
        <Button
          variant="ghost"
          size="icon"
          className={cn("ml-auto h-8 w-8 text-gray-400 hover:text-white hover:bg-white/10", collapsed && "hidden")}
          onClick={() => setCollapsed(true)}
          data-testid="collapse-sidebar-btn"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1 px-3 py-3">
        {/* Dashboards */}
        {!collapsed ? (
          <button onClick={() => setDashOpen(!dashOpen)} className="flex items-center justify-between w-full px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-300 transition-colors">
            <span>Dashboards</span>
            <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", dashOpen && "rotate-90")} />
          </button>
        ) : <Separator className="my-2 bg-gray-800" />}
        {(dashOpen || collapsed) && (
          <div className="space-y-0.5 mt-0.5">
            {dashboards.map((item) => (
              <NavItem key={item.name} item={item} collapsed={collapsed} location={location} onMobileClose={handleMobileClose} />
            ))}
          </div>
        )}

        {/* Operações */}
        {!collapsed ? (
          <button onClick={() => setOpsOpen(!opsOpen)} className="flex items-center justify-between w-full px-3 py-1.5 mt-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-300 transition-colors">
            <span>Operações</span>
            <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", opsOpen && "rotate-90")} />
          </button>
        ) : <Separator className="my-2 bg-gray-800" />}
        {(opsOpen || collapsed) && (
          <div className="space-y-0.5 mt-0.5">
            {operations.map((item) => (
              <NavItem key={item.name} item={item} collapsed={collapsed} location={location} onMobileClose={handleMobileClose} />
            ))}
          </div>
        )}

        {/* AIOps */}
        {!collapsed ? (
          <button onClick={() => setAiOpsOpen(!aiOpsOpen)} className="flex items-center justify-between w-full px-3 py-1.5 mt-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-300 transition-colors">
            <span>AIOps</span>
            <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", aiOpsOpen && "rotate-90")} />
          </button>
        ) : <Separator className="my-2 bg-gray-800" />}
        {(aiOpsOpen || collapsed) && (
          <div className="space-y-0.5 mt-0.5">
            {aiops.map((item) => (
              <NavItem key={item.name} item={item} collapsed={collapsed} location={location} onMobileClose={handleMobileClose} />
            ))}
          </div>
        )}

        {/* Web Monitor */}
        {!collapsed ? (
          <button onClick={() => setWebOpen(!webOpen)} className="flex items-center justify-between w-full px-3 py-1.5 mt-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-300 transition-colors">
            <span>Web Monitor</span>
            <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", webOpen && "rotate-90")} />
          </button>
        ) : <Separator className="my-2 bg-gray-800" />}
        {(webOpen || collapsed) && (
          <div className="space-y-0.5 mt-0.5">
            {webmonitor.map((item) => (
              <NavItem key={item.name} item={item} collapsed={collapsed} location={location} onMobileClose={handleMobileClose} badges={badges} />
            ))}
          </div>
        )}

        {/* Base de Conhecimento */}
        {!collapsed ? (
          <button onClick={() => setKbOpen(!kbOpen)} className="flex items-center justify-between w-full px-3 py-1.5 mt-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-300 transition-colors">
            <span>Base de Conhecimento</span>
            <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", kbOpen && "rotate-90")} />
          </button>
        ) : <Separator className="my-2 bg-gray-800" />}
        {(kbOpen || collapsed) && (
          <div className="space-y-0.5 mt-0.5">
            {knowledge.map((item) => (
              <NavItem key={item.name} item={item} collapsed={collapsed} location={location} onMobileClose={handleMobileClose} />
            ))}
          </div>
        )}

        {/* Gerenciamento */}
        {!isClient && (!collapsed ? (
          <button onClick={() => setMgmtOpen(!mgmtOpen)} className="flex items-center justify-between w-full px-3 py-1.5 mt-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-300 transition-colors">
            <span className="flex items-center gap-2">
              Gerenciamento
              {badges.orgPending > 0 && <Badge variant="destructive" className="h-4 px-1 text-[9px]">{badges.orgPending}</Badge>}
            </span>
            <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", mgmtOpen && "rotate-90")} />
          </button>
        ) : <Separator className="my-2 bg-gray-800" />)}
        {!isClient && (mgmtOpen || collapsed) && (
          <div className="space-y-0.5 mt-0.5">
            {management.map((item) => (
              <NavItem key={item.name} item={item} collapsed={collapsed} location={location} onMobileClose={handleMobileClose} badges={badges} />
            ))}
          </div>
        )}
      </ScrollArea>

      {collapsed && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute -right-3 top-20 h-6 w-6 rounded-full border border-gray-700 bg-gray-900 text-gray-400 hover:text-white shadow-md hidden md:flex"
          onClick={() => setCollapsed(false)}
          data-testid="expand-sidebar-btn"
        >
          <ChevronRight className="h-3 w-3" />
        </Button>
      )}
    </div>
  );

  return (
    <TooltipProvider>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile toggle button */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-3 left-3 z-50 md:hidden h-10 w-10 bg-gray-900 border border-gray-700 text-gray-400 hover:text-white shadow-sm"
        onClick={() => setMobileOpen(!mobileOpen)}
        data-testid="mobile-menu-btn"
      >
        {mobileOpen ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
      </Button>

      {/* Mobile sidebar */}
      <div 
        className={cn(
          "fixed left-0 top-0 h-full border-r border-gray-800 z-50 transition-transform duration-300 md:hidden w-64",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
        style={{ backgroundColor: sidebarBg || '#000' }}
      >
        {sidebarContent}
      </div>

      {/* Desktop sidebar */}
      <div 
        className={cn(
          "fixed left-0 top-0 h-full border-r border-gray-800 z-40 transition-all duration-300 hidden md:block",
          collapsed ? "w-16" : "w-64"
        )}
        style={{ backgroundColor: sidebarBg || '#000' }}
      >
        {sidebarContent}
      </div>
    </TooltipProvider>
  );
}
